/**
 * renderMarkdown — converte Markdown in HTML sicuro.
 *
 * Sicurezza: tutto il testo in input viene HTML-escaped prima di qualsiasi
 * trasformazione. Solo i tag generati internamente (strong, em, code, pre,
 * ul, li, h1-h3, br) vengono emessi — nessun input dell'utente o della AI
 * può iniettare HTML raw.
 *
 * Supporta:
 *   ```code block```       → <pre><code>…</code></pre>
 *   `inline code`          → <code>…</code>
 *   **bold**               → <strong>…</strong>
 *   *italic*               → <em>…</em>
 *   - item / • item        → <ul><li>…</li></ul>
 *   ### / ## / # Header    → <h3> / <h2> / <h1>
 *   newline                → <br> (fuori dai blocchi)
 *
 * Per aggiungere in futuro: link, tabelle, checkbox list.
 */

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function renderMarkdown(text: string): string {
  if (!text) return ''

  // ── 1. Estrai e proteggi i code block (``` … ```) ────────────────────────
  const codeBlocks: string[] = []
  let s = text.replace(/```(\w*)\n?([\s\S]*?)```/g, (_match, _lang, code) => {
    const idx = codeBlocks.length
    codeBlocks.push(`<pre><code>${escapeHtml(code.trimEnd())}</code></pre>`)
    return `\x00BLOCK${idx}\x00`
  })

  // ── 2. Escape HTML nel testo rimanente ───────────────────────────────────
  s = escapeHtml(s)

  // ── 3. Inline code (`…`) ─────────────────────────────────────────────────
  s = s.replace(/`([^`\n]+)`/g, '<code>$1</code>')

  // ── 4. Bold (**…**) ──────────────────────────────────────────────────────
  s = s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')

  // ── 5. Italic (*…*) ──────────────────────────────────────────────────────
  s = s.replace(/\*(.+?)\*/g, '<em>$1</em>')

  // ── 6. Headers (###, ##, #) ──────────────────────────────────────────────
  s = s.replace(/^### (.+)$/gm, '<h3>$1</h3>')
  s = s.replace(/^## (.+)$/gm,  '<h2>$1</h2>')
  s = s.replace(/^# (.+)$/gm,   '<h1>$1</h1>')

  // ── 7. Liste non ordinate (- item, • item) ───────────────────────────────
  s = s.replace(/^[•\-] (.+)$/gm, '<li>$1</li>')
  // Raggruppa li consecutivi in <ul>
  s = s.replace(/(<li>[\s\S]*?<\/li>\n?)+/g, (match) => `<ul>${match}</ul>`)

  // ── 8. Newline → <br> (solo testo normale, non dentro blocchi) ───────────
  s = s.replace(/\n/g, '<br>')

  // ── 9. Ripulisci <br> intorno a elementi blocco ───────────────────────────
  const blockTags = '(?:ul|li|h[123]|pre)'
  s = s.replace(new RegExp(`<br>(</?${blockTags}[^>]*>)`, 'g'), '$1')
  s = s.replace(new RegExp(`(</?${blockTags}[^>]*>)<br>`, 'g'), '$1')

  // ── 10. Ripristina code block ─────────────────────────────────────────────
  s = s.replace(/\x00BLOCK(\d+)\x00/g, (_m, i) => codeBlocks[parseInt(i)])

  return s
}
